/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import Scroll from '@component/common/scroll';
import Line from '@component/common/line';
import * as size from '@constant/size';
import { isMobile } from 'react-device-detect';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import TextInput from '@component/common/input/text-input';
import * as design from '@constant/design';
import { createErrorText } from '@util/string';
import { useTypedSelector } from '@hook/common/useStore';
import ImageBlock from '@component/common/block/image-block';
import IconBlock from '@component/common/block/icon-block';
import { useAuthInput, useAuthPage, useFindMe, usePassword, useSignout } from '@hook/auth/useAuth';
import { getConnectionAccount } from '@util/image';
import { EAuthType } from '@datatype/type';
import { useNavigate } from 'react-router-dom';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding: ${size.PAGE_PADDING};
`;

const MyPageKey = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  //justify-content: space-between;
  justify-content: flex-start;
  gap: 10px;
  align-items: end;
  width: 100%;
  height: 20px;
`;

const MyPageValue = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  height: 20px;
`;

const OptionButton = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
  align-items: flex-end;
`;

const ConnectionAccountLogo = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  margin: 5px 0 0 0;
  gap: 5px;
`;

const PasswordContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: relative;
`;

const AccountContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  padding: ${size.MARGIN_1} 0;
`;

const OPTION_TEXT_BUTTON_DESIGN = {
  text: { size: 'small', color: color.GRAY_1 },
  box: { event: { cursor: 'pointer' } },
} as const;
const ITEM_TEXT_DESIGN = {
  text: { size: 'medium', bold: true },
  box: { width: '100%', alignX: 'left', margin: `0 0 5px 0` },
} as const;
const KEY_TEXT_DESIGN = { text: { size: 'medium', bold: false } } as const;
const VALUE_TEXT_DESIGN = { text: { size: 'medium', height: '25px', color: color.GRAY_1 } } as const;
const PASSWORD_DESIGN = {
  text: { size: 'medium' },
  box: {
    width: '100%',
    height: '30px',
    border: true,
    borderColor: color.GRAY_1,
    alignX: 'left',
    padding: `0 ${size.MARGIN_4} 0 ${size.MARGIN_1}`,
  },
} as const;
const PASSWORD_VISIBILITY_ICON_DESIGN = {
  icon: { size: 'medium', kind: 'outlined', color: color.GRAY_1 },
  box: { right: size.MARGIN_1, cursor: 'pointer' },
} as const;

const GAP_HEIGHT = '50px';

const Account = () => {
  const navigate = useNavigate();
  const { signout } = useSignout();
  const { openAuthPage } = useAuthPage();
  const { currentPassword, newPassword, edit } = useAuthInput();
  const { changePassword } = usePassword();
  const me = useFindMe();

  const authType = me?.data?.authType.toLowerCase() || '';
  const email = me?.data?.email ?? '';
  const username = me?.data?.username ?? '';
  const errorCode = useTypedSelector((state) => state.rootReducer?.authReducer?.errorCode) || null;

  const handlePasswordSaveClick = async () => {
    const result = await changePassword(currentPassword.value, newPassword.value);
    if (!result) return;
    edit.password.completeHandler();
  };
  const handleRemoveMeClick = () => openAuthPage('signout');
  const handleLogoutClick = async () => await signout();

  const { name: accountName, logo: accountLogo } = getConnectionAccount(authType!);

  return (
    <Wrapper>
      <Scroll direction='y' height={`calc(100%)`}>
        <TextBlock text='계정정보' design={ITEM_TEXT_DESIGN} />
        <Line color={color.GRAY_2} />
        <AccountContentContainer>
          <MyPageKey>
            <TextBlock text='아이디' design={KEY_TEXT_DESIGN} />
          </MyPageKey>
          <MyPageValue>
            <TextBlock text={username} design={VALUE_TEXT_DESIGN} />
          </MyPageValue>
          <Gap height={size.MARGIN_1} />
          <MyPageKey>
            <TextBlock text='이메일' design={KEY_TEXT_DESIGN} />
          </MyPageKey>
          <MyPageValue>
            <TextBlock text={email} design={VALUE_TEXT_DESIGN} />
          </MyPageValue>
          {authType !== EAuthType.INHOUSE && (
            <>
              <Gap height={size.MARGIN_1} />
              <MyPageKey>
                <TextBlock text='연결된 계정' design={KEY_TEXT_DESIGN} />
              </MyPageKey>
              <MyPageValue>
                <ConnectionAccountLogo>
                  <ImageBlock src={accountLogo} alt='logo' design={{ image: { height: '18px' } }} />
                  <TextBlock text={accountName} design={VALUE_TEXT_DESIGN} />
                </ConnectionAccountLogo>
              </MyPageValue>
            </>
          )}
        </AccountContentContainer>
        <Line color={color.GRAY_3} />
        <Gap height={GAP_HEIGHT} />
        <TextBlock text='구독' design={ITEM_TEXT_DESIGN} />
        <Line color={color.GRAY_2} />
        <AccountContentContainer>
          <TextBlock
            text='구독 관리'
            design={{
              text: { size: 'medium', color: color.BLACK_2, bold: false },
              box: { event: { cursor: 'pointer' } },
            }}
            click={{ handler: () => navigate(IYAGIGO_PATH.HUB_SUBSCRIPTION_PATH) }}
          />
        </AccountContentContainer>
        <Line color={color.GRAY_3} />
        {authType === EAuthType.INHOUSE && (
          <>
            <Gap height={GAP_HEIGHT} />
            <TextBlock text='계정설정' design={ITEM_TEXT_DESIGN} />
            <Line color={color.GRAY_2} />
            <AccountContentContainer>
              {authType === 'inhouse' && (
                <>
                  <MyPageKey>
                    <TextBlock text='비밀번호' design={KEY_TEXT_DESIGN} />
                    <OptionButton>
                      {edit.password.isEdit ? (
                        <>
                          <TextBlock
                            text='저장하기'
                            design={OPTION_TEXT_BUTTON_DESIGN}
                            click={{ handler: handlePasswordSaveClick }}
                          />
                          <Gap width={'10px'} />
                          <TextBlock
                            text='취소하기'
                            design={OPTION_TEXT_BUTTON_DESIGN}
                            click={{ handler: edit.password.cancelHandler }}
                          />
                        </>
                      ) : (
                        <TextBlock
                          text='수정하기'
                          design={OPTION_TEXT_BUTTON_DESIGN}
                          click={{ handler: edit.password.editHandler }}
                        />
                      )}
                    </OptionButton>
                  </MyPageKey>
                  {edit.password.isEdit && (
                    <>
                      <Gap height={'5px'} />
                      <PasswordContainer>
                        <TextInput
                          name={'currentPassword'}
                          placeholder={'현재 비밀번호'}
                          design={PASSWORD_DESIGN}
                          onChange={currentPassword.handler}
                          value={currentPassword.value}
                          type={currentPassword.visible ? 'text' : 'password'}
                        />
                        <IconBlock
                          icon={currentPassword.visible ? 'visibility-on' : 'visibility-off'}
                          design={PASSWORD_VISIBILITY_ICON_DESIGN}
                          click={{ handler: currentPassword.visibleHandler }}
                        />
                      </PasswordContainer>
                      <Gap height={'5px'} />
                      <PasswordContainer>
                        <TextInput
                          name={'newPassword'}
                          placeholder={'새 비밀번호'}
                          design={PASSWORD_DESIGN}
                          onChange={newPassword.handler}
                          value={newPassword.value}
                          type={newPassword.visible ? 'text' : 'password'}
                        />
                        <IconBlock
                          icon={newPassword.visible ? 'visibility-on' : 'visibility-off'}
                          design={PASSWORD_VISIBILITY_ICON_DESIGN}
                          click={{ handler: newPassword.visibleHandler }}
                        />
                      </PasswordContainer>
                    </>
                  )}
                </>
              )}
            </AccountContentContainer>
          </>
        )}
        <Line color={color.GRAY_3} />
        <TextBlock
          text={createErrorText(errorCode)}
          design={{
            ...design.ERROR_TEXT_DESIGN,
            box: {
              ...design.ERROR_TEXT_DESIGN,
              margin: `${size.MARGIN_1} 0 0 0`,
              // height: `calc(${GAP_HEIGHT} * 1)`,
              alignX: 'left',
            },
          }}
        />
        <Gap height={GAP_HEIGHT} />
        {/*<TextBlock text='계정·구독관리' design={ITEM_TEXT_DESIGN} />*/}
        <TextBlock text='계정관리' design={ITEM_TEXT_DESIGN} />
        <Line color={color.GRAY_2} />
        <AccountContentContainer>
          <TextBlock
            text='로그아웃하기'
            design={{
              text: { size: 'medium', color: color.BLACK_2, bold: false },
              box: { event: { cursor: 'pointer' } },
            }}
            click={{ handler: handleLogoutClick }}
          />
          <Line color={color.GRAY_3} margin={`${size.MARGIN_1} 0`} />
          <TextBlock
            text='탈퇴하기'
            design={{
              text: { size: 'medium', color: color.BLACK_2, bold: false },
              box: { event: { cursor: 'pointer' } },
            }}
            click={{ handler: handleRemoveMeClick }}
          />
        </AccountContentContainer>
        <Line color={color.GRAY_3} />
      </Scroll>
    </Wrapper>
  );
};

export default Account;
